import {Injectable} from '@angular/core';
import {Company} from '../models/company';
import {PageCrud} from '../Enum/page-crud.enum';
import {UserLoggedRolesServiceService} from '../authenticationModule/services/user-logged-roles-service.service';

@Injectable()
export class UserUtilsService {

  constructor(private userLoggedRolesServiceService: UserLoggedRolesServiceService) {
  }

  isClientCompany(company: Company): boolean {
    return !!company && company.uniqid !== 'admin';
  }

  userHasDifferentDomain(acknowledgeDomains: string, emailSuffix: string): boolean {
    if (!acknowledgeDomains) {
      return true;
    }
    const domains = acknowledgeDomains.split(',').map(domain => domain.trim().toUpperCase());
    return !domains.includes(emailSuffix.trim().toUpperCase());
  }

  isNdaMissingOrExpired(company: Company, now: Date = new Date()): boolean {
    if (!company || !this.isClientCompany(company)) {
      return false;
    }
    if (company.ndaExecutionDate === null || company.ndaExecutionDate === undefined) {
      return true;
    }
    if (company.ndaExpirationDate === null || company.ndaExpirationDate === undefined) {
      return false;
    }
    return new Date(company.ndaExpirationDate) < now;
  }

  appendAcknowledgedDomain(acknowledgeDomains: string, domain: string): string {
    if (!domain) {
      return acknowledgeDomains || '';
    }
    if (!acknowledgeDomains) {
      return domain;
    }
    const existingDomains = acknowledgeDomains.split(',').map(item => item.trim().toUpperCase());
    if (existingDomains.includes(domain.trim().toUpperCase())) {
      return acknowledgeDomains;
    }
    return acknowledgeDomains + ',' + domain;
  }

  canEditUser(currentUser: any, targetUser: any): boolean {
    if (!currentUser || !currentUser.permissions || !currentUser.permissions.canEditUsers) {
      return false;
    }
    // A non-super client cannot edit a super client.
    if (this.userLoggedRolesServiceService.userHasRole(targetUser, 'ROLE_CLIENT_SUPER')
      && !this.userLoggedRolesServiceService.userHasOneOfThoseRoles(currentUser, ['ROLE_MCKAY', 'ROLE_CLIENT_SUPER'])) {
      return false;
    }
    // A non-super mckay user cannot edit a super mckay user.
    return !(this.userLoggedRolesServiceService.userHasRole(targetUser, 'ROLE_MCKAY_SUPER')
      && !this.userLoggedRolesServiceService.userHasRole(currentUser, 'ROLE_MCKAY_SUPER'));
  }

  canChangeSuperRole(currentUser: any, targetUser: any, isUserInSameCompany: boolean, page: PageCrud): boolean {
    if (!currentUser || !targetUser || targetUser.isNotificationOnly) {
      return false;
    }
    return this.userLoggedRolesServiceService.userHasOneOfThoseRoles(currentUser, ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN'])
      && !isUserInSameCompany
      && page === PageCrud.EDIT
      && !this.userLoggedRolesServiceService.userHasRole(targetUser, 'ROLE_CLIENT_SUPER');
  }

  shouldShowDistributionListButtons(page: PageCrud, currentUser: any, loggedInUser: any): boolean {
    if (page !== PageCrud.EDIT || !currentUser || !loggedInUser) {
      return false;
    }
    if (this.userLoggedRolesServiceService.userHasOneOfThoseRoles(currentUser, ['ROLE_MCKAY_SUPER', 'ROLE_CLIENT_SUPER'])) {
      return false;
    }
    if (currentUser.uniqid === loggedInUser.uniqid) {
      return false;
    }
    return this.userLoggedRolesServiceService.userHasOneOfThoseRoles(loggedInUser,
      ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN', 'ROLE_CLIENT_SUPER', 'ROLE_CLIENT_ADMIN']);
  }

  isValidPhone(countryCode: string, nationalNumber: string): boolean {
    return this.isValidPhoneCountryCode(countryCode, nationalNumber) && this.isValidPhoneNationalNumber(countryCode, nationalNumber);
  }

  isValidPhoneCountryCode(countryCode: string, nationalNumber: string): boolean {
    if (countryCode === '') {
      return nationalNumber === '';
    }
    return this.isValidNumber(countryCode);
  }

  isValidPhoneNationalNumber(countryCode: string, nationalNumber: string): boolean {
    if (nationalNumber === '') {
      return countryCode === '';
    }
    return this.isValidNumber(nationalNumber);
  }

  parsePhoneNumber(countryCode: string, nationalNumber: string): string {
    if (!countryCode || !nationalNumber) {
      return '';
    }
    return '+(' + countryCode + ') ' + nationalNumber;
  }

  extractCountryCode(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }

    // +(33) 972130951 => 33
    // +(33)972130951 => 33
    const leftBracketIndex = phoneNumber.indexOf('(');
    const rightBracketIndex = phoneNumber.indexOf(')');
    if (leftBracketIndex >= 0 && rightBracketIndex >= 0) {
      return phoneNumber.substring(leftBracketIndex + 1, rightBracketIndex);
    }
    // +33 972130951 => 33
    const plusIndex = phoneNumber.indexOf('+');
    const spaceIndex = phoneNumber.indexOf(' ');
    if (plusIndex >= 0 && spaceIndex >= 0) {
      return phoneNumber.substring(plusIndex + 1, spaceIndex);
    }
    // 33 972130951 => 33
    if (spaceIndex >= 0) {
      return phoneNumber.substring(0, spaceIndex);
    }
    // +33972130951 => unknown
    // 33972130951 => unknown
    return '';
  }

  extractNationalNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }
    // +(33) 972130951 => 972130951
    // +33) 972130951 => 972130951
    // +33 972130951 => 972130951
    // 33 972130951 => 972130951
    const spaceIndex = phoneNumber.indexOf(' ');
    if (spaceIndex >= 0) {
      return phoneNumber.substring(spaceIndex + 1);
    }

    // +(33)972130951 => 972130951
    // +33)972130951 => 972130951
    const rightBracketIndex = phoneNumber.indexOf(')');
    if (rightBracketIndex >= 0) {
      return phoneNumber.substring(rightBracketIndex + 1);
    }

    // +33972130951 => +33972130951
    // 33972130951 => 33972130951
    return phoneNumber;
  }

  private isValidNumber(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    // Number is not mandatory to add a user.
    return !isNaN(Number(value));
  }
}
